import { useCallback } from 'react';

import { createSlice } from '@reduxjs/toolkit';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';

import { PayloadAction } from '~/techstyle-shared/redux-core';

import { cartApi } from '../services/cartApi';

export type PromoCodeErrorType =
  | 'ERROR_INVALID'
  | 'ERROR_INVALID_GIFT_CERT'
  | 'ERROR_UNAUTHORIZED'
  | 'ERROR_UNKNOWN';

export interface PromoCodeState {
  error: PromoCodeErrorType | null;
  showSuccess: boolean;
}

const initialState: PromoCodeState = {
  error: null,
  showSuccess: false,
};

export const promoCode = createSlice({
  name: 'promoCode',
  initialState,
  reducers: {
    resetPromoCodeInputState: () => {
      return initialState;
    },
    clearPromoCodeInputError: (state) => {
      state.error = null;
    },

    setShowSuccess: (state, action: PayloadAction<boolean>) => {
      state.showSuccess = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      cartApi.endpoints.applyPromoCode.matchPending,
      (state) => {
        state.error = null;
        state.showSuccess = false;
      }
    );
    builder.addMatcher(
      cartApi.endpoints.applyPromoCode.matchFulfilled,
      (state) => {
        state.showSuccess = true;
        state.error = null;
      }
    );
    builder.addMatcher(
      cartApi.endpoints.applyPromoCode.matchRejected,
      (state, action) => {
        state.showSuccess = false;
        const promoCode = action.meta?.arg?.originalArgs?.promoCode;
        // same condition as PromoCode.js
        const isGiftCard = promoCode?.length >= 12;

        // @ts-ignore
        switch (action.payload.response?.status) {
          case 400:
            state.error = isGiftCard
              ? 'ERROR_INVALID_GIFT_CERT'
              : 'ERROR_INVALID';
            break;
          case 401:
            state.error = 'ERROR_UNAUTHORIZED';
            break;
          default:
            state.error = 'ERROR_UNKNOWN';
        }
      }
    );
    builder.addMatcher(
      cartApi.endpoints.removePromoCode.matchPending,
      (state) => {
        state.error = null;
        state.showSuccess = false;
      }
    );
    builder.addMatcher(
      cartApi.endpoints.removePromoCode.matchFulfilled,
      (state) => {
        state.showSuccess = true;
        state.error = null;
      }
    );
  },
});

export const { resetPromoCodeInputState, clearPromoCodeInputError } =
  promoCode.actions;

export const selectShowSuccess = (state: RootStateOrAny) =>
  state.promoCode.showSuccess;
export const selectPromoCodeError = (state: RootStateOrAny) =>
  state.promoCode.error;

export const useClearPromoCodeError = () => {
  const error = useSelector(selectPromoCodeError);
  const dispatch = useDispatch();
  return useCallback(() => {
    if (error !== null) {
      dispatch(clearPromoCodeInputError());
    }
  }, [dispatch, error]);
};
